import React from 'react';
import { createFeature } from '@edreamsodigeo/frontend-core';
import { useLogin } from '@/providers/login/LoginProvider';
import { PRIME_FIRST_PHASE_2_DESKTOP } from '@/versionedComponents';
import { useIsPrimeUser } from '@/hooks/useIsPrimeUser';
import { OFFERS, PAYMENT } from '@/hooks/usePriceFreezeRouter';
import {
  FIELDS,
  FIELDS_WITH_CONFIRM,
} from '@/components/SummaryContactDetails/formUtils';
import { HomepageComponent } from '@/components/HomePage/HomepageComponent';
import { useResetTMSTracking } from '@/hooks/useTcVars';
import {
  useActionsToOffersPage,
  useActionsToPaymentPage,
  useActionsToPaymentPageWithAutorenewal,
  useOnContinueSuccess,
} from './HomePageIRUM.hooks';
import { withFlowComponent } from '@/HOC/withFlowComponent';
import { featuredAutoRenewalOnInDeposit } from '@/featuredComponents';

export const VersionedMobileHomepage = featuredAutoRenewalOnInDeposit([
  HomePagePrimeFirstPhase2,
  HomePageAutorenewal,
]);

const VersionedDesktopHomepage = createFeature({
  name: PRIME_FIRST_PHASE_2_DESKTOP,
  variations: [
    HomePageRecognize,
    HomePagePrimeFirstPhase2,
    HomePagePrimeFirstPhase2,
  ],
  version: 1,
});

export const HomePageIRUM = withFlowComponent(
  HomePageIRUMMobile,
  HomePageIRUMDesktop
);

function HomePageIRUMMobile(props) {
  return <VersionedMobileHomepage {...props} />;
}

function HomePageIRUMDesktop(props) {
  return <VersionedDesktopHomepage {...props} />;
}

function HomePageRecognize({ searchId, fareItineraryKey, segmentKeys }, props) {
  // when the user clicks CONTINUE:
  // - create the SB
  // - create the PF product
  // - navigate to Payment page
  const isPrimeUser = useIsPrimeUser();
  const onContinueSuccess = useOnContinueSuccess({
    searchId,
    fareItineraryKey,
    segmentKeys,
  });

  const { loading, error, onContinueClick } = useActionsToPaymentPage({
    onContinueSuccess: () => onContinueSuccess(PAYMENT),
  });

  return (
    <HomepageComponent
      {...props}
      formFields={FIELDS}
      loading={loading}
      error={error}
      validation={() => Boolean(isPrimeUser)}
      onContinue={onContinueClick}
    />
  );
}

function HomePagePrimeFirstPhase2(
  { searchId, fareItineraryKey, segmentKeys },
  props
) {
  // when the user clicks CONTINUE:
  // - create the SB
  // - if the user is Prime or recognized Prime
  // - - create the price freeze product
  // - - navigate to Payment page
  // - else
  // - - add the contact details to the SB
  // - - navigate to Offers page
  const { isLogged } = useLogin();
  const isPrimeUser = useIsPrimeUser();
  useResetTMSTracking(isPrimeUser);
  const onContinueSuccess = useOnContinueSuccess({
    searchId,
    fareItineraryKey,
    segmentKeys,
  });

  // Behavior for non logged users
  const {
    loading: loadingOffers,
    error: errorOffers,
    onContinueClick: onContinueClickToOffersPage,
  } = useActionsToOffersPage({
    onContinueSuccess: () => onContinueSuccess(OFFERS),
  });
  ///////////////////////////////
  // Behavior for prime and recognized prime users
  const {
    loading: loadingPayment,
    error: errorPayment,
    onContinueClick: onContinueClickToPaymentPage,
  } = useActionsToPaymentPage({
    onContinueSuccess: () => onContinueSuccess(PAYMENT),
  });
  //

  return (
    <HomepageComponent
      {...props}
      formFields={isLogged ? FIELDS : FIELDS_WITH_CONFIRM}
      loading={loadingOffers || loadingPayment}
      error={errorOffers || errorPayment}
      onContinue={(formData) => {
        if (isPrimeUser) {
          onContinueClickToPaymentPage(formData);
        } else {
          onContinueClickToOffersPage(formData);
        }
      }}
    />
  );
}

function HomePageAutorenewal(
  { searchId, fareItineraryKey, segmentKeys },
  props
) {
  // when the user clicks CONTINUE:
  // - if the user is Prime and
  // - - autorenewal is OFF
  // - - - create the SB
  // - - - create the price freeze product
  // - - - update the user account
  // - - autorenewal is ON
  // - - - create the SB
  // - - - create the price freeze product
  // - if the user is not Prime
  // - - shows HomePagePrimeFirstPhase2 component
  const { isPrime } = useLogin();
  const isPrimeUser = useIsPrimeUser();
  useResetTMSTracking(isPrimeUser);
  const onContinueSuccess = useOnContinueSuccess({
    searchId,
    fareItineraryKey,
    segmentKeys,
  });

  // Behavior for Prime with Autorenewal OFF
  const {
    loading: loadingAutorenewal,
    error: errorAutorenewal,
    onContinueClick: onContinueClickToPaymentPageWithAutorenewal,
  } = useActionsToPaymentPageWithAutorenewal({
    onContinueSuccess: () => onContinueSuccess(PAYMENT),
  });
  // Behavior for Prime without changing Autorenewal status
  const {
    loading: loadingPayment,
    error: errorPayment,
    onContinueClick: onContinueClickToPaymentPage,
  } = useActionsToPaymentPage({
    onContinueSuccess: () => onContinueSuccess(PAYMENT),
  });
  //

  return !isPrime ? (
    <HomePagePrimeFirstPhase2
      searchId={searchId}
      fareItineraryKey={fareItineraryKey}
      segmentKeys={segmentKeys}
      {...props}
    />
  ) : (
    <HomepageComponent
      {...props}
      formFields={FIELDS}
      loading={loadingAutorenewal || loadingPayment}
      error={errorAutorenewal || errorPayment}
      onContinue={onContinueClickToPaymentPage}
      onContinueWithAutorenewal={onContinueClickToPaymentPageWithAutorenewal}
    />
  );
}
